import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  PlayArrow as StartIcon,
  Stop as StopIcon,
  ContentCopy as CopyIcon,
  OpenInNew as OpenIcon,
  Tv as TvIcon,
  Timer as TimerIcon,
} from "@mui/icons-material";
import io from "socket.io-client";
import Header from "./Header";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const getAdminToken = () =>
  localStorage.getItem("admin_token") || localStorage.getItem("auth_token");

const SpectatorControl = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingAutoOff, setSavingAutoOff] = useState(false);
  const [currentState, setCurrentState] = useState(null);
  const [gameTypes, setGameTypes] = useState([]);
  const [selectedGame, setSelectedGame] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [message, setMessage] = useState(null);
  const [autoOffEnabled, setAutoOffEnabled] = useState(false);
  const [autoOffHours, setAutoOffHours] = useState(2);
  const socketRef = useRef(null);

  const spectateUrl = `${window.location.origin}/spectate`;

  const fetchState = useCallback(async () => {
    try {
      const token = getAdminToken();
      const [stateRes, typesRes, autoOffRes] = await Promise.all([
        fetch(`${API_BASE}/api/spectator/live`),
        fetch(`${API_BASE}/api/spectator/game-types`),
        fetch(`${API_BASE}/api/spectator/auto-off`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (stateRes.ok) {
        const data = await stateRes.json();
        setCurrentState(data);
        if (data.status === "live") {
          setSelectedGame(data.game_type || "");
          setSessionId(data.session_id || "");
        }
      }
      if (typesRes.ok) {
        const data = await typesRes.json();
        setGameTypes(data.game_types || []);
      }
      if (autoOffRes.ok) {
        const data = await autoOffRes.json();
        setAutoOffEnabled(!!data.enabled);
        setAutoOffHours(parseInt(data.hours, 10) || 2);
      }
    } catch (err) {
      console.error("Failed to fetch spectator state:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchState();
  }, [fetchState]);

  useEffect(() => {
    const socket = io(API_BASE, { transports: ["websocket"] });
    socketRef.current = socket;
    socket.on("connect", () => {
      socket.emit("spectator:join");
    });
    socket.on("spectator:updated", (payload) => {
      setCurrentState(payload);
      if (payload?.status === "live") {
        setSelectedGame(payload.game_type || "");
        setSessionId(payload.session_id || "");
      }
    });
    return () => {
      socket.emit("spectator:leave");
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const handleStart = async () => {
    if (!selectedGame) {
      setMessage({ type: "error", text: "Please select a game type" });
      return;
    }

    setSaving(true);
    setMessage(null);
    try {
      const body = {
        game_type: selectedGame,
        session_id: sessionId || null,
        config: {},
      };

      const res = await fetch(`${API_BASE}/api/spectator/start`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify(body),
      });

      const data = await res.json();
      if (data.success) {
        setCurrentState(data);
        setMessage({ type: "success", text: "Spectator view started!" });
      } else {
        setMessage({ type: "error", text: data.error || "Failed to start" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Network error" });
    } finally {
      setSaving(false);
    }
  };

  const handleStop = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/spectator/stop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAdminToken()}`,
        },
      });
      const data = await res.json();
      if (data.success) {
        setCurrentState(data);
        setMessage({ type: "success", text: "Spectator view stopped" });
      } else {
        setMessage({ type: "error", text: data.error || "Failed to stop" });
      }
    } catch (err) {
      setMessage({ type: "error", text: "Network error" });
    } finally {
      setSaving(false);
    }
  };

  const saveAutoOff = async (enabled, hours) => {
    const nextHours = Math.min(24, Math.max(1, parseInt(hours, 10) || 1));
    setSavingAutoOff(true);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/spectator/auto-off`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${getAdminToken()}`,
        },
        body: JSON.stringify({ enabled, hours: nextHours }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.success) {
        throw new Error(data.error || "Failed to save auto-off");
      }
      setAutoOffEnabled(!!data.enabled);
      setAutoOffHours(data.hours);
      if (data.live) {
        setCurrentState(data.live);
      }
      setMessage({
        type: "success",
        text: data.enabled
          ? `Auto off enabled — spectator view will stop after ${data.hours} hour${data.hours === 1 ? "" : "s"}`
          : "Auto off disabled",
      });
    } catch (err) {
      setMessage({ type: "error", text: err.message || "Failed to save auto-off" });
    } finally {
      setSavingAutoOff(false);
    }
  };

  const copyUrl = () => {
    navigator.clipboard.writeText(spectateUrl).then(() => {
      setMessage({ type: "success", text: "URL copied to clipboard!" });
    });
  };

  const selectedGameInfo = gameTypes.find((g) => g.id === selectedGame);
  const isLive = currentState?.status === "live";
  const autoOffAtLabel =
    isLive && currentState?.auto_off_at
      ? new Date(currentState.auto_off_at).toLocaleString()
      : null;

  if (loading) {
    return (
      <>
        <Header />
        <Container maxWidth="md" sx={{ mt: 4, display: "flex", justifyContent: "center" }}>
          <CircularProgress sx={{ color: "#9C27B0" }} />
        </Container>
      </>
    );
  }

  return (
    <>
      <Header />
      <Container maxWidth="md" sx={{ mt: 3, mb: 4 }}>
        <Paper sx={{ bgcolor: "#1a1a1a", borderRadius: 3, p: { xs: 2, md: 4 } }}>
          {/* Title */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
            <TvIcon sx={{ color: "#E040FB", fontSize: 32 }} />
            <Box>
              <Typography variant="h5" sx={{ color: "#fff", fontWeight: 700 }}>
                Spectator View
              </Typography>
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>
                Control what viewers see on the public spectator screen
              </Typography>
            </Box>
          </Box>

          {/* Status */}
          <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Chip
              label={isLive ? "LIVE" : "IDLE"}
              sx={{
                bgcolor: isLive ? "rgba(0,200,83,0.15)" : "rgba(158,158,158,0.15)",
                color: isLive ? "#00c853" : "#9e9e9e",
                border: `1px solid ${isLive ? "#00c853" : "#9e9e9e"}`,
                fontWeight: 700,
                fontSize: "0.85rem",
              }}
            />
            {isLive && currentState.game_type && (
              <Chip
                label={gameTypes.find((g) => g.id === currentState.game_type)?.label || currentState.game_type}
                size="small"
                sx={{ bgcolor: "rgba(224,64,251,0.15)", color: "#E040FB", border: "1px solid #E040FB" }}
              />
            )}
            {isLive && currentState.started_at && (
              <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.8rem" }}>
                Started: {new Date(currentState.started_at).toLocaleTimeString()}
              </Typography>
            )}
            {autoOffAtLabel && (
              <Typography sx={{ color: "#FFB74D", fontSize: "0.8rem" }}>
                Auto off: {autoOffAtLabel}
              </Typography>
            )}
          </Box>

          {/* Spectator URL */}
          <Paper sx={{ bgcolor: "#111", border: "1px solid #333", borderRadius: 2, p: 2, mb: 3 }}>
            <Typography sx={{ color: "rgba(255,255,255,0.6)", fontSize: "0.75rem", mb: 0.5, fontWeight: 600 }}>
              SPECTATOR URL
            </Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography sx={{ color: "#fff", fontSize: "0.95rem", flex: 1, wordBreak: "break-all" }}>
                {spectateUrl}
              </Typography>
              <Tooltip title="Copy URL">
                <IconButton size="small" onClick={copyUrl} sx={{ color: "#aaa" }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Open in new tab">
                <IconButton size="small" onClick={() => window.open(spectateUrl, "_blank")} sx={{ color: "#aaa" }}>
                  <OpenIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          <Divider sx={{ borderColor: "#333", mb: 3 }} />

          {/* Controls */}
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5 }}>
            {/* Game selection */}
            <FormControl fullWidth>
              <InputLabel sx={{ color: "rgba(255,255,255,0.6)" }}>Select Game</InputLabel>
              <Select
                value={selectedGame}
                onChange={(e) => setSelectedGame(e.target.value)}
                label="Select Game"
                disabled={isLive}
                sx={{
                  color: "#fff",
                  ".MuiOutlinedInput-notchedOutline": { borderColor: "#444" },
                  "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "#666" },
                  "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#9C27B0" },
                }}
              >
                {gameTypes.map((gt) => (
                  <MenuItem key={gt.id} value={gt.id}>
                    {gt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedGame === "insta_quiz" && !isLive && (
              <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem" }}>
                Active Insta Quiz session is detected automatically when viewers open /spectate.
              </Typography>
            )}

            {selectedGameInfo?.needs_session && !selectedGameInfo?.auto_session && (
              <TextField
                fullWidth
                label="Session ID (optional)"
                value={sessionId}
                onChange={(e) => setSessionId(e.target.value)}
                disabled={isLive}
                placeholder="Leave empty for auto-detect where supported"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#444" },
                    "&:hover fieldset": { borderColor: "#666" },
                    "&.Mui-focused fieldset": { borderColor: "#9C27B0" },
                  },
                  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
                }}
              />
            )}

            {/* Action buttons */}
            <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
              {!isLive ? (
                <Button
                  variant="contained"
                  startIcon={<StartIcon />}
                  onClick={handleStart}
                  disabled={saving || !selectedGame}
                  sx={{
                    bgcolor: "#00c853",
                    "&:hover": { bgcolor: "#00a844" },
                    fontWeight: 700,
                    px: 4,
                  }}
                >
                  {saving ? "Starting..." : "Start Spectator View"}
                </Button>
              ) : (
                <Button
                  variant="contained"
                  startIcon={<StopIcon />}
                  onClick={handleStop}
                  disabled={saving}
                  sx={{
                    bgcolor: "#d50000",
                    "&:hover": { bgcolor: "#b71c1c" },
                    fontWeight: 700,
                    px: 4,
                  }}
                >
                  {saving ? "Stopping..." : "Stop Spectator View"}
                </Button>
              )}
            </Box>
          </Box>

          <Divider sx={{ borderColor: "#333", my: 3 }} />

          {/* Auto off — applies to every spectator game */}
          <Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <TimerIcon sx={{ color: "#FFB74D" }} />
              <Typography sx={{ color: "#fff", fontWeight: 700 }}>
                Auto off Spectator view
              </Typography>
            </Box>
            <Typography sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", mb: 2 }}>
              Applies to every spectator game. When enabled, the live view turns off automatically
              after the set hours. Starting a view begins the timer; changing hours while live
              restarts it from now.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={autoOffEnabled}
                  disabled={savingAutoOff}
                  onChange={(e) => saveAutoOff(e.target.checked, autoOffHours)}
                  sx={{
                    "& .MuiSwitch-switchBase.Mui-checked": { color: "#FFB74D" },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: "#FFB74D",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ color: "#fff" }}>
                  {autoOffEnabled ? "Auto off is ON" : "Auto off is OFF"}
                </Typography>
              }
            />

            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, mt: 1.5, flexWrap: "wrap" }}>
              <TextField
                type="number"
                label="Hours"
                value={autoOffHours}
                disabled={!autoOffEnabled || savingAutoOff}
                onChange={(e) => setAutoOffHours(e.target.value)}
                onBlur={() => {
                  const next = Math.min(24, Math.max(1, parseInt(autoOffHours, 10) || 1));
                  setAutoOffHours(next);
                }}
                inputProps={{ min: 1, max: 24, step: 1 }}
                helperText="1–24 hours from start"
                sx={{
                  width: 160,
                  "& .MuiOutlinedInput-root": {
                    color: "#fff",
                    "& fieldset": { borderColor: "#444" },
                    "&:hover fieldset": { borderColor: "#666" },
                    "&.Mui-focused fieldset": { borderColor: "#FFB74D" },
                  },
                  "& .MuiInputLabel-root": { color: "rgba(255,255,255,0.6)" },
                  "& .MuiFormHelperText-root": { color: "rgba(255,255,255,0.4)" },
                }}
              />
              <Button
                variant="outlined"
                disabled={!autoOffEnabled || savingAutoOff}
                onClick={() => saveAutoOff(true, autoOffHours)}
                sx={{
                  mt: 0.5,
                  color: "#FFB74D",
                  borderColor: "rgba(255,183,77,0.5)",
                  "&:hover": { borderColor: "#FFB74D", bgcolor: "rgba(255,183,77,0.08)" },
                }}
              >
                {savingAutoOff ? "Saving..." : "Save hours"}
              </Button>
            </Box>
          </Box>

          {/* Messages */}
          {message && (
            <Alert severity={message.type} sx={{ mt: 3 }} onClose={() => setMessage(null)}>
              {message.text}
            </Alert>
          )}
        </Paper>
      </Container>
    </>
  );
};

export default SpectatorControl;
